import * as React from "react";
import { Input, Popover, Tag } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectContext, selectOrg, setGvc } from "../../../../store/contextSlice";
import { fetchGvcs, selectGvcs, selectOrgs } from "../../../../store/userDataSlice";
import { CheckOutlined } from "@ant-design/icons";
import { Gvc } from "../../../../schema/types/gvc/entity";
import { CONSOLE_URL } from "../../../../envVariables";

interface Props extends React.PropsWithChildren {
  isOpen: boolean;
  setIsOpen: any;
}
export const ContextGvcDrawer: React.FC<Props> = ({ isOpen, setIsOpen, children }) => {
  const dispatch = useAppDispatch();
  const context = useAppSelector(selectContext);
  const orgs = useAppSelector(selectOrgs);
  const gvcs = useAppSelector(selectGvcs);

  const [gvcSearch, setGvcSearch] = React.useState<string>("");
  const filterRef = React.useRef(null as any);
  const [programmaticHover, setProgrammaticHover] = React.useState("");

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (filterRef.current) {
      setTimeout(() => {
        filterRef.current.focus();
      }, 0);
    }
    dispatch(fetchGvcs());
  }, [isOpen]);

  if (!context.org) {
    return null;
  }

  const orgItem = orgs.find((orgItem) => orgItem.name === context.org);

  const gvcItems = React.useMemo(() => {
    return gvcs.filter((gvcItem) => {
      if (!gvcSearch) {
        return true;
      }
      return gvcItem.name.toLowerCase().includes(gvcSearch.toLowerCase());
    });
  }, [gvcs, gvcSearch]);

  React.useEffect(() => {
    if (!gvcSearch) {
      setProgrammaticHover("");
      return;
    }

    const firstGvc = gvcs.find((gvcItem) => gvcItem.name.toLowerCase().includes(gvcSearch.toLowerCase()));
    if (firstGvc) {
      setProgrammaticHover(firstGvc.name);
    }
  }, [gvcSearch]);

  React.useEffect(() => {
    if (!programmaticHover) {
      const el = document.querySelector(`.drawer-filter-section`);
      if (el) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }

    const el = document.getElementById(`gvc-item-${programmaticHover}`);
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [programmaticHover]);

  function handleKeyDown(e) {
    const code = e.code;
    if (code === "Escape") {
      if (gvcSearch) {
        setGvcSearch("");
      } else {
        setIsOpen(false);
        setTimeout(() => {
          const el = document.querySelector(`.context-widget-gvc-parent`);
          if (el) {
            (el as any).focus();
          }
        });
      }
    } else if (code === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();

      if (programmaticHover) {
        const index = gvcItems.findIndex((g) => g.name === programmaticHover);
        const nextItem = gvcItems[index + 1];
        if (nextItem) {
          setProgrammaticHover(nextItem.name);
        } else {
          setProgrammaticHover("");
        }
      } else if (gvcItems.length > 0) {
        setProgrammaticHover(gvcItems[0].name);
      }
    } else if (code === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();

      if (programmaticHover) {
        const index = gvcItems.findIndex((g) => g.name === programmaticHover);
        const previousItem = gvcItems[index - 1];
        if (previousItem) {
          setProgrammaticHover(previousItem.name);
        } else {
          setProgrammaticHover("");
        }
      } else if (gvcItems.length > 0) {
        const lastItem = gvcItems[gvcItems.length - 1];
        if (lastItem) {
          setProgrammaticHover(lastItem.name);
        }
      }
    } else if (code === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      if (programmaticHover) {
        const item = gvcItems.find((i) => i.name === programmaticHover);
        if (item) {
          dispatch(setGvc(item.name));
          setIsOpen(false);
          setTimeout(() => {
            const el = document.querySelector(`.context-widget-gvc-parent`);
            if (el) {
              (el as any).focus();
            }
          });
        }
      }
    }
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => setIsOpen(open)}
      trigger={["click"]}
      content={() => {
        if (!orgItem) {
          return <div>Org item selected for context is not found.</div>;
        }

        return (
          <div
            style={{
              minWidth: "320px",
              maxWidth: "320px",
              maxHeight: "calc(50svh - 82px)",
              overflow: "auto",
            }}
            className="py-1 context-gvc-drawer"
            onKeyDown={handleKeyDown}
          >
            <div className="drawer-filter-section flex items-center gap-4 mb-4 px-2">
              <div className="font-bold">GVCs</div>
              <Input
                ref={filterRef}
                style={{ maxWidth: 250 }}
                value={gvcSearch}
                onChange={(e) => setGvcSearch(e.target.value)}
                allowClear
                placeholder="Filter"
              />
            </div>

            {gvcItems.length > 0 ? (
              <div className="flex flex-col">
                <a
                  id={`gvc-item-all`}
                  href={`${CONSOLE_URL}/console/org/${context.org}`}
                  className={`px-4 py-2 flex flex-col gap-4 cursor-pointer gvc-item ${
                    !programmaticHover ? "gvc-item-programmatic-hover" : ""
                  } ${context.gvc ? "" : "gvc-item-active"} w-full`}
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey || e.button === 1) {
                      return;
                    }
                    e.preventDefault();
                    dispatch(setGvc(""));
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">All GVCs</div>
                    {!context.gvc ? <CheckOutlined /> : null}
                  </div>
                </a>
                {gvcItems.map((gvcItem, index) => {
                  const isEven = index % 2 === 0;
                  const isCurrent = context.gvc === gvcItem.name;

                  return (
                    <GvcItem
                      key={`gvc-${gvcItem.name}`}
                      gvc={gvcItem}
                      isEven={isEven}
                      isCurrent={isCurrent}
                      onDone={() => setIsOpen(false)}
                      programmaticHover={programmaticHover === gvcItem.name}
                    />
                  );
                })}
              </div>
            ) : (
              <span>This Org has no GVC</span>
            )}
          </div>
        );
      }}
    >
      {children}
    </Popover>
  );
};

interface GvcItemProps {
  isCurrent: boolean;
  isEven: boolean;
  gvc: Gvc;
  onDone: any;
  programmaticHover: boolean;
}
function GvcItem({ isCurrent, isEven, gvc, onDone, programmaticHover }: GvcItemProps) {
  const dispatch = useAppDispatch();
  const org = useAppSelector(selectOrg);

  return (
    <a
      key={`${org}-${gvc.name}`}
      id={`gvc-item-${gvc.name}`}
      href={`${CONSOLE_URL}/console/org/${org}/gvc/${gvc.name}`}
      className={`${isCurrent ? "gvc-item-active" : ""} px-4 py-2 flex flex-col gap-4 cursor-pointer gvc-item ${
        programmaticHover ? "gvc-item-programmatic-hover" : ""
      } ${isEven ? "gvc-item-even" : "gvc-item-odd"} w-full`}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey || e.button === 1) {
          return;
        }
        e.preventDefault();
        dispatch(setGvc(gvc.name));
        onDone();
      }}
    >
      <div className="flex items-center justify-between">
        <div className="font-semibold">{gvc.name}</div>
        {isCurrent ? <CheckOutlined /> : null}
      </div>
    </a>
  );
}
